import { useState } from "react";
import { Shield, Trash2, Download, ChevronRight } from "lucide-react";
import {
  downloadGdprDataExport,
  eraseChatbotUserConversations,
} from "../../api/portal";
import { formatApiErrorDetail } from "../../utils/format";

/**
 * RGPD : export des données liées aux conversations et effacement de l'historique
 * pour un compte d'accès chatbot (le traitement est effectué côté serveur).
 */
export default function PortalDataRightsSection({
  api,
  chatbotUsers,
  onRefresh,
}) {
  const [userId, setUserId] = useState("");
  const [busy, setBusy] = useState(false);

  const uid = userId ? parseInt(userId, 10) : null;
  const selected = chatbotUsers.find((u) => u.id === uid);
  const canAct = Number.isInteger(uid) && uid > 0 && selected && !busy;

  const handleExport = async () => {
    if (!canAct) return;
    setBusy(true);
    try {
      await downloadGdprDataExport(api, uid);
      onRefresh?.();
    } catch (err) {
      alert(formatApiErrorDetail(err, "Export impossible"));
    } finally {
      setBusy(false);
    }
  };

  const handleErase = async () => {
    if (!canAct) return;
    const ok = window.confirm(
      `Effacer tout l'historique des conversations pour « ${selected.username} » ?\n\n` +
        "Cette action supprime définitivement les messages et titres de conversation. " +
        "Le compte d'accès reste actif sauf si vous le supprimez dans l'onglet « Accès Chatbot ».",
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await eraseChatbotUserConversations(api, uid);
      const n = res.data?.conversations_deleted ?? 0;
      alert(
        n > 0
          ? `Historique effacé : ${n} conversation${n > 1 ? "s" : ""} supprimée${n > 1 ? "s" : ""}.`
          : "Aucune conversation à effacer pour cet accès.",
      );
      onRefresh?.();
    } catch (err) {
      alert(formatApiErrorDetail(err, "Effacement impossible"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <details
      className="group max-w-2xl rounded-xl border border-stone-200/80 bg-stone-50/40 open:bg-white open:shadow-sm transition-colors"
      aria-labelledby="portal-data-rights-heading"
    >
      <summary className="cursor-pointer list-none flex items-center gap-2 px-4 py-3 text-sm text-stone-600 hover:text-stone-800 select-none [&::-webkit-details-marker]:hidden">
        <ChevronRight
          className="size-4 shrink-0 text-stone-400 transition-transform group-open:rotate-90"
          aria-hidden
        />
        <Shield className="size-4 shrink-0 text-stone-400" aria-hidden />
        <span
          id="portal-data-rights-heading"
          className="font-medium text-stone-700"
        >
          Données utilisateurs (export / effacement)
        </span>
      </summary>

      <div className="px-4 pb-4 pt-0 space-y-4 border-t border-stone-100/80">
        <p className="text-xs text-stone-500 leading-relaxed pt-3">
          Export et effacement portent uniquement sur l&apos;historique de chat lié au
          compte choisi (votre organisation uniquement, vérifiée côté serveur). Les mots de
          passe ne sont pas exportés. Les fichiers du portail documents ne sont pas inclus.
        </p>

        {chatbotUsers.length === 0 ? (
          <p className="text-xs text-stone-500 rounded-lg border border-dashed border-stone-200/80 bg-white/60 px-3 py-2.5">
            Aucun accès chatbot : créez-en un dans « Accès Chatbot ».
          </p>
        ) : (
          <>
            <div className="space-y-1.5">
              <label
                htmlFor="data-rights-user"
                className="text-xs font-medium text-stone-600"
              >
                Accès concerné
              </label>
              <select
                id="data-rights-user"
                className="w-full max-w-md px-3 py-2 rounded-lg bg-white border border-stone-200/90 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-stone-300/80 focus:border-stone-300"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                disabled={busy}
              >
                <option value="">Choisir…</option>
                {chatbotUsers.map((u) => (
                  <option key={u.id} value={String(u.id)}>
                    {u.username}
                    {u.subclient_name ? ` — ${u.subclient_name}` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
              <button
                type="button"
                onClick={handleExport}
                disabled={!canAct}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 bg-white text-stone-700 text-xs font-medium hover:bg-stone-50 disabled:opacity-45 disabled:cursor-not-allowed"
              >
                <Download className="size-3.5 shrink-0 opacity-70" aria-hidden />
                Export JSON
              </button>
              <button
                type="button"
                onClick={handleErase}
                disabled={!canAct}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200/80 bg-white text-red-800/90 text-xs font-medium hover:bg-red-50/80 disabled:opacity-45 disabled:cursor-not-allowed"
              >
                <Trash2 className="size-3.5 shrink-0 opacity-70" aria-hidden />
                Effacer les conversations
              </button>
            </div>

            <p className="text-[11px] text-stone-400 leading-relaxed">
              Supprimer totalement un compte : onglet « Accès Chatbot ». L'équipe
              indiquée ci-dessus sert au périmètre d&apos;accès aux documents ; elle ne crée
              pas de fuite de données entre entreprises.
            </p>
          </>
        )}
      </div>
    </details>
  );
}
